/**
 * What offline export does not reproduce.
 *
 * Export renders through its own OfflineAudioContext graph. That graph builds
 * the same insert chains, group buses and sends as the mixer, so the file is
 * the mix that was heard — with one exception.
 *
 * The VST bridge hosts plugins in a separate process in real time, so it
 * cannot render into an offline context at all. That needs a different
 * mechanism (real-time bounce), and until it exists export says plainly which
 * tracks are affected: silently handing someone the wrong render is the
 * actual defect.
 */

/**
 * @param {object} project
 * @param {Array}  project.tracks      timeline tracks
 * @param {object} project.trackFxMap  { [trackId]: FxSlot[] }
 * @returns {{ complete: boolean, vstTracks: string[], summary: string|null }}
 */
export function describeExportCoverage(project = {}) {
  const tracks = Array.isArray(project.tracks) ? project.tracks : [];
  const trackFxMap = project.trackFxMap && typeof project.trackFxMap === 'object' ? project.trackFxMap : {};

  const nameOf = (trackId) => {
    const track = tracks.find((t) => t?.id === trackId);
    return track?.name || trackId;
  };

  const vstTracks = [];
  Object.entries(trackFxMap).forEach(([trackId, slots]) => {
    if (!Array.isArray(slots)) return;
    // A bypassed slot is not part of the mix, so it is not a discrepancy.
    const active = slots.filter((slot) => slot && slot.enabled !== false);
    if (active.some((slot) => slot.type === 'vst' || slot.pluginId)) vstTracks.push(nameOf(trackId));
  });

  const complete = vstTracks.length === 0;
  const summary = complete ? null
    : `This export will not include the VST plugins on ${vstTracks.join(', ')}: they run in a separate process in real time and cannot be rendered offline. `
      + 'Everything else — built-in effects, buses and sends, track volume and pan, mute/solo and the master limiter — renders as you hear it.';

  return { complete, vstTracks, summary };
}

export default describeExportCoverage;
